import { BaseEntity } from '@/api/EntityApi';
import { DeepKeys } from '@tanstack/react-table';
import { Asset } from '@/api/AssetApi';
import LANGUAGES from '@/configs/languages';
import APP_CONFIG from '@/configs/appConfig';
import { Preset } from '@/api/PresetApi';

export enum Gender {
	MALE,
	FEMALE,
	BOTH,
}

export type AppCurrency = (typeof APP_CONFIG.CURRENCIES)[number];

export type Language = keyof typeof LANGUAGES;
export type ForeignLanguage = Exclude<Language, 'PL'>;
export type Translations<TData> = Record<ForeignLanguage, TData>;

export type SortOrder = 'ASC' | 'DESC';

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: SortOrder;
}

export interface ApiQueryOptions<TData extends BaseEntity> {
	page: number;
	limit: number;
	search: string;
	sortBy: Sort<TData>;
}

export type CustomFilters = Record<string, unknown>;

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export interface CreateManyResponse {
	passed: string[];
	rejected: string[];
}

export interface Attachment {
	id: UUID;
	path: string;
	name: string;
	size: number;
	mimetype: string;
}

export type UUID = ReturnType<typeof crypto.randomUUID>;

export enum CreatorActionType {
	SET_MATERIAL = 'SET_MATERIAL',
}

export type CreatorAction = {
	id: UUID;
	attributeValue: BaseEntity;
	type: CreatorActionType;
	payload: CreatorActionPayload;
};

export type CreatorActionPayload = {
	asset?: Asset;
	target?: string;
};

export enum MessageDataTitle {
	TOGGLE = 'TOGGLE',
	RESET = 'RESET',
	SAVE_PRESET = 'SAVE_PRESET',
}

export type SavePresetData = {
	id: UUID;
	collectionId: UUID;
	renderer: Record<string, any>;
	storeConfigurations: Preset['storeConfigurations'];
};

export type MessageDataPayload =
	| {
			title: MessageDataTitle.TOGGLE;
			isOpen: boolean;
	  }
	| {
			title: MessageDataTitle.RESET;
	  }
	| {
			title: MessageDataTitle.SAVE_PRESET;
			data: SavePresetData;
	  };

export enum ObjectAvailability {
	ACTIVE = 'ACTIVE',
	INACTIVE = 'INACTIVE',
}
